"use client";

import { usePostHog } from "posthog-js/react";
import { FC, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import {
  ActionCTAField,
  BaseFieldComponentError,
  ICreateAccountActionResponseSuccess,
  SectionContinueAction,
} from "@forms/schema";
import Button from "apps/website/components/base/Button/Button";
import {
  useNewFormsServiceStore,
  getFlowFieldValuesForAction,
  getFlowProduct,
} from "libs/state/src/lib/stores/useFormServiceStore";
import { useCatsStore, useCustomerStore, useDiscountStore } from "@./state";
import { useAPI } from "apps/website/hooks/useAPI";
import { getStoryBlokLink } from "apps/website/utils/storyblok/links";
import { useQueryParams } from "apps/website/hooks/useQueryParams";
import { useAnalytics } from "apps/website/hooks/useAnalytics";
import {
  FORM_LAST_EMAIL,
  FORM_LAST_OLD_USER_ID,
  FORM_LAST_USER_ID,
  useDatadog,
} from "@auth/client-sdk-react";
import { TokenSuccessResponse } from "@auth/types";
import { AuthSDK } from "@auth/client-sdk";

import { CheckoutStatus } from "@/primary-models";
import { DisplayState } from "@/constants/state";

import { FormServiceCTA } from "../../fields/CTA";
import {
  ACTION_TRACKING_COOKIE_NAMES,
  getActionTrackingInfo,
} from "../../../../utils/tracking/getActionTrackingInfo";
import {
  getShreddedAaSetOnceProperty,
} from "../../../../utils/tracking/shreddedAaPersonProperties";

export interface IFlowContinue {
  onClick(): void;
  onError(error?: BaseFieldComponentError): void;
  isLastSectionInFlow: boolean;
  formContinueWithAction?: SectionContinueAction;
  field: ActionCTAField;
  flowId: string;
  isDisabled: boolean;
  continueText: string;
  alternative: boolean;
}

const continueButtonTestId = "flowContinueButton";
type ResponseWithGtmData = { gtmData?: unknown };

export const FlowContinue: FC<IFlowContinue> = ({
  onClick,
  onError,
  isLastSectionInFlow,
  formContinueWithAction,
  field,
  flowId,
  isDisabled,
  continueText,
  alternative,
}) => {
  const formsActionsAPI = useAPI().FormActions;
  const checkoutAPI = useAPI().Checkout;
  const { cats, setCats } = useCatsStore();
  const { customerIds, userIds, setCustomerId, setUserId } = useCustomerStore();
  const { discountCodes } = useDiscountStore();

  const [ buttonState, setButtonState ] = useState(DisplayState.READY);
  const [ linkState, setLinkState ] = useState(DisplayState.READY);
  const { chainQueryParamsToUrl } = useQueryParams();

  const { setFlowFieldError, setFlowError, clearFlowErrors } =
    useNewFormsServiceStore();

  const posthog = usePostHog();
  const datadog = useDatadog();
  const [ cookies ] = useCookies(ACTION_TRACKING_COOKIE_NAMES);

  const analytics = useAnalytics();

  const handleLinkState = () => {
    clearFlowErrors(flowId);
    setLinkState(DisplayState.PROCESSING);
  };

  useEffect(() => {
    setLinkState(DisplayState.READY);
  }, []);

  const pollCheckout = async (basketId: string) => {
    const checkoutResponse = await checkoutAPI.pollBasket(basketId);
    if (checkoutResponse?.status !== CheckoutStatus.SUCCESS) {
      setFlowError(flowId, "Influencer Checkout failed");
      throw new Error("Influencer Checkout failed");
    }
  };

  const continueAndAction = async () => {
    clearFlowErrors(flowId);
    try {
      setButtonState(DisplayState.PROCESSING);
      const featureFlags = posthog.featureFlags.getFlagVariants();
      const fieldSubmitValuesMap = getFlowFieldValuesForAction(flowId);
      const catsArr = cats
        ? cats?.find((dc) => dc.flowId === flowId)?.catIds || []
        : [];
      fieldSubmitValuesMap.set("catIds", { value: catsArr });
      fieldSubmitValuesMap.set("customerId", {
        value: customerIds.find((dc) => dc.flowId === flowId)?.customerId || "",
      });
      fieldSubmitValuesMap.set("userId", {
        value: userIds.find((dc) => dc.flowId === flowId)?.userId || "",
      });
      fieldSubmitValuesMap.set("discountCode", {
        value:
          discountCodes.find((dc) => dc.flowId === flowId)?.discountCode ?? "",
      });
      if (formContinueWithAction?.action) {
        const res = await formsActionsAPI.performAction<unknown>(
          formContinueWithAction?.action,
          fieldSubmitValuesMap,
          getFlowProduct(flowId),
          flowId,
          undefined,
          featureFlags,
          getActionTrackingInfo(cookies),
          datadog.logger,
        );
        if (!res.success) {
          if (res.message) {
            if (res.failedOn) {
              onError({
                message: res.message,
                name: res?.failedOn?.fieldName as string,
              });
              setFlowFieldError(flowId, res.failedOn.fieldName ?? "", {
                message: res.message,
              });
            } else {
              setFlowError(flowId, res.message);
              window.scroll({
                top: 0,
                left: 0,
              });
            }
          }
          setButtonState(DisplayState.ERROR);
        } else {
          if (res.responseBody) {
            if ((res.responseBody as { catIds: string[] })?.catIds) {
              if ((res.responseBody as { catIds: string[] })?.catIds.length) {
                setCats(
                  flowId,
                  (res.responseBody as { catIds: string[] }).catIds,
                );
              }
            }
            if ((res.responseBody as { customerId: string })?.customerId) {
              setCustomerId(
                flowId,
                (res.responseBody as { customerId: string }).customerId,
              );
            }
            if ((res.responseBody as { userId: string })?.userId) {
              setUserId(
                flowId,
                (res.responseBody as { userId: string }).userId,
              );
            }

            const responseWithGtmData = res.responseBody as ResponseWithGtmData;
            if (responseWithGtmData?.gtmData && globalThis.window?.dataLayer) {
              try {
                const { gtmData } = responseWithGtmData;
                if (
                  typeof gtmData === "object" &&
                  gtmData &&
                  "event" in gtmData
                ) {
                  datadog?.logger?.info(
                    "FlowContinue.continueAndAction window.dataLayer.push",
                    {
                      gtmDataEvent: gtmData.event,
                      formName: flowId,
                      gtmData,
                    },
                  );
                }
                globalThis.window.dataLayer.push(gtmData);
              } catch (e) {
                console.error(
                  `Failed to push gtmData in response for action '${formContinueWithAction?.action}'`,
                );
                console.error(e);
              }
            }

            if (
              typeof res.responseBody === "object" &&
              "authData" in
                (res.responseBody as { authData: TokenSuccessResponse })
            ) {
              const { authData } = res.responseBody as {
                authData: TokenSuccessResponse;
              };
              if (authData) {
                const payload = AuthSDK.decodePayload(authData.access_token);
                if (!payload) {
                  datadog.logger.error(
                    "Failed to decode access token payload",
                    { authData },
                  );
                  return;
                }
                const { firstName, lastName, email, old, iold, sub, isub } =
                  payload;

                const oldUserId = iold || old!;
                const newUserId = isub || sub;
                if (oldUserId) {
                  void analytics
                    .identify(oldUserId, {
                      email,
                      name: `${firstName} ${lastName}`,
                    })
                    .catch((e) => {
                      console.error("Failed to identify user", e);
                    });

                  const personProperties = {
                    email,
                    firstName,
                    lastName,
                  };
                  posthog.identify(oldUserId, personProperties);
                }

                if (newUserId) {
                  datadog.setGlobalContextProperty(
                    FORM_LAST_USER_ID,
                    newUserId,
                  );
                  localStorage.setItem(FORM_LAST_USER_ID, newUserId);
                }
                datadog.setGlobalContextProperty(FORM_LAST_EMAIL, email);
                localStorage.setItem(FORM_LAST_EMAIL, email);
                if (oldUserId) {
                  datadog.setGlobalContextProperty(
                    FORM_LAST_OLD_USER_ID,
                    oldUserId,
                  );
                  localStorage.setItem(FORM_LAST_OLD_USER_ID, oldUserId);
                }
              }
            }

            if (
              typeof res.responseBody === "object" &&
              "accountData" in
                (res.responseBody as ICreateAccountActionResponseSuccess)
            ) {
              const { accountData } =
                res.responseBody as ICreateAccountActionResponseSuccess;
              if (accountData) {
                const {
                  firstName,
                  lastName,
                  email,
                  old: oldUserId,
                  sub: newUserId,
                } = accountData;

                if (oldUserId) {
                  void analytics
                    .identify(oldUserId, {
                      email,
                      name: `${firstName} ${lastName}`,
                    })
                    .catch((e) => {
                      console.error("Failed to identify user", e);
                    });

                  const personProperties = {
                    email,
                    firstName,
                    lastName,
                  };
                  const shreddedAaSetOnceProps = getShreddedAaSetOnceProperty({ posthog });

                  if (shreddedAaSetOnceProps) {
                    posthog.identify(
                      oldUserId,
                      personProperties,
                      shreddedAaSetOnceProps,
                    );
                  } else {
                    posthog.identify(oldUserId, personProperties);
                  }
                }

                if (newUserId) {
                  datadog.setGlobalContextProperty(
                    FORM_LAST_USER_ID,
                    newUserId,
                  );
                  localStorage.setItem(FORM_LAST_USER_ID, newUserId);
                }
                datadog.setGlobalContextProperty(FORM_LAST_EMAIL, email);
                localStorage.setItem(FORM_LAST_EMAIL, email);
                if (oldUserId) {
                  datadog.setGlobalContextProperty(
                    FORM_LAST_OLD_USER_ID,
                    oldUserId,
                  );
                  localStorage.setItem(FORM_LAST_OLD_USER_ID, oldUserId);
                }
              }
            }

            if (
              formContinueWithAction?.action === "freshInfluencerCheckout" &&
              typeof res.responseBody === "object" &&
              "basketId" in res.responseBody &&
              typeof res.responseBody.basketId === "string"
            ) {
              await pollCheckout(res.responseBody.basketId);
            }
          }
          if (formContinueWithAction.navigate_to?.url) {
            if (
              formContinueWithAction.navigate_to.url.includes(
                "/influencer-freebox/welcome",
              )
            ) {
              globalThis.window.location.href = `${chainQueryParamsToUrl(
                getStoryBlokLink(formContinueWithAction.navigate_to),
              )}?katkinCustomerId=${
                customerIds.find((dc) => dc.flowId === flowId)?.customerId
              }&katkinUserId=${
                userIds.find((dc) => dc.flowId === flowId)?.userId
              }`;
            } else {
              globalThis.window.location.href = chainQueryParamsToUrl(
                getStoryBlokLink(formContinueWithAction.navigate_to),
              );
            }
          } else {
            onClick();
            setButtonState(DisplayState.READY);
          }
        }
      } else {
        datadog.logger.error("Flow continue missing action!", {
          flowId,
          formContinueWithAction,
          featureFlags,
          fieldSubmitValuesMap,
          catsArr,
        });
        setButtonState(DisplayState.ERROR);
      }
    } catch (error) {
      datadog.logger.error(
        "Flow continue error",
        { flowId, formContinueWithAction },
        error as Error,
      );
      setButtonState(DisplayState.ERROR);
    }
  };

  const continueDefault = () => {
    clearFlowErrors(flowId);
    onClick();
  };

  if (formContinueWithAction) {
    if (formContinueWithAction.action === "navigateTo") {
      const navigateTo = formContinueWithAction.navigate_to
        ? chainQueryParamsToUrl(
          getStoryBlokLink(formContinueWithAction.navigate_to),
        )
        : undefined;

      return (
        <Button
          to={navigateTo}
          disabled={isDisabled}
          state={linkState}
          onClick={handleLinkState}
          data-testid={continueButtonTestId}
        >
          { formContinueWithAction.name }
        </Button>
      );
    }

    return (
      <Button
        onClick={() => {
          void continueAndAction();
        }}
        disabled={isDisabled}
        state={buttonState}
        data-testid={continueButtonTestId}
      >
        { formContinueWithAction.name }
      </Button>
    );
  }

  if (isLastSectionInFlow) {
    return (
      <FormServiceCTA
        field={field}
        flowId={flowId}
        disabled={isDisabled}
        className="w-full"
        data-testid={continueButtonTestId}
      />
    );
  }

  return (
    <Button
      onClick={continueDefault}
      disabled={isDisabled}
      design={alternative ? "underline" : "default"}
      data-testid={continueButtonTestId}
    >
      { continueText }
    </Button>
  );
};
